import { createContext, useState } from 'react'
import type { FC, ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { theme } from '../theme'

type Topic = { id: string; title: string; color: string }
type Category = { id: string; title: string }
type SubCategory = { id: string; title: string }
type Material = { id: string; title: string }
export interface SearchParamsContextValue {
  searchParams: URLSearchParams
  removeParam: (key: SearchParamKey) => void
  activeTopic: Topic | null
  activeCategory: Category | null
  activeSubCategory: SubCategory | null
  activeMaterial: Material | null
  setActiveTopic: (id: string, title: string, color: string | null, clearChildren?: boolean) => void
  setActiveCategory: (id: string, title: string) => void
  setActiveSubCategory: (id: string, title: string) => void
  setActiveMaterial: (id: string, title: string) => void
  clearParams: () => void
}

interface SearchParamsProviderProps {
  children?: ReactNode
}

const SearchParamsContext = createContext<SearchParamsContextValue>({
  searchParams: new URLSearchParams(),
  activeTopic: null,
  activeCategory: null,
  activeSubCategory: null,
  activeMaterial: null,
  removeParam: () => {},
  setActiveTopic: () => {},
  setActiveCategory: () => {},
  setActiveSubCategory: () => {},
  setActiveMaterial: () => {},
  clearParams: () => {}
})

const clearSearchParams = (newParam: SearchParamKey, allParams: [SearchParamKey, string][]) => {
  const clearedParams = [...allParams]

  switch (newParam) {
    case 'tema':
      // Don't automatically clear category/subcategory - let setActiveTopic control this
      return clearedParams
    case 'category':
      return clearedParams.filter((value) => value[0] !== 'subcategory')
    default:
      return clearedParams
  }
}

export const SearchParamsProvider: FC<SearchParamsProviderProps> = ({ children }) => {
  const [oldSearchParams, setSearchParams] = useSearchParams()
  const [activeTopic, setActiveTop] = useState<Topic | null>(null)
  const [activeCategory, setActiveCat] = useState<Category | null>(null)
  const [activeSubCategory, setActiveSubCat] = useState<SubCategory | null>(null)
  const [activeMaterial, setActiveMat] = useState<Material | null>(null)

  const clearParams = () => {
    setSearchParams([])
    setActiveTop(null)
    setActiveCat(null)
    setActiveSubCat(null)
    setActiveMat(null)
  }

  const setParam = (key: SearchParamKey, value: string) => {
    let newParams: [SearchParamKey, string][] = []
    let found = false
    oldSearchParams.forEach((v, k) => {
      if (k === key) {
        found = true
        newParams.push([key, value])
      } else {
        newParams.push([k as SearchParamKey, v])
      }
    })

    if (!found) newParams.push([key, value])

    const clearedParams = clearSearchParams(key, newParams)

    setSearchParams(clearedParams)
  }

  const removeParam = (key: string) => {
    const newParams = new URLSearchParams(oldSearchParams)
    newParams.delete(key)
    setSearchParams(newParams)
  }

  const setActiveTopic = (
    id: string,
    title: string,
    color: string | null,
    clearChildren: boolean = true
  ) => {
    if (id === '') {
      clearParams()
      return
    }

    setParam('tema', id)
    setActiveTop({ id, title, color: color ?? theme.palette.background.default })

    // Only clear category/subcategory if clearChildren is true (default behavior)
    if (clearChildren) {
      setActiveCat(null)
      setActiveSubCat(null)
      // Manually remove category/subcategory from URL when clearing children
      removeParam('category')
      removeParam('subcategory')
    }
  }

  const setActiveCategory = (id: string, title: string) => {
    if (id === '') {
      removeParam('category')
      setActiveCat(null)
      return
    }
    // Ensure the topic parameter is also set in the URL if we have an active topic
    if (activeTopic) {
      setParam('tema', activeTopic.id)
    }
    setParam('category', id)
    setActiveCat({ id, title })
    setActiveSubCat(null)
  }

  const setActiveSubCategory = (id: string, title: string) => {
    if (id === '') {
      removeParam('subcategory')
      setActiveSubCat(null)
      return
    }
    // Ensure the topic and category parameters are also set in the URL
    if (activeTopic) {
      setParam('tema', activeTopic.id)
    }
    if (activeCategory) {
      setParam('category', activeCategory.id)
    }
    setParam('subcategory', id)
    setActiveSubCat({ id, title })
  }

  const setActiveMaterial = (id: string, title: string) => {
    if (id === '') {
      removeParam('material')
      setActiveMat(null)
      return
    }
    setParam('material', id)
    setActiveMat({ id, title })
  }

  return (
    <SearchParamsContext.Provider
      value={{
        searchParams: oldSearchParams,
        activeTopic,
        activeCategory,
        activeSubCategory,
        activeMaterial,
        setActiveTopic,
        setActiveCategory,
        setActiveSubCategory,
        setActiveMaterial,
        removeParam,
        clearParams
      }}
    >
      {children}
    </SearchParamsContext.Provider>
  )
}

export const SearchParamsConsumer = SearchParamsContext.Consumer

export default SearchParamsContext
